import { useMemo, useRef, useState } from 'react'
import useSound from 'use-sound'
import { ImpactStyle, NotificationType } from '@capacitor/haptics'
import { HapticPattern, triggerHaptic } from 'src/utils/helpers/haptics'

type GameAudioOptions = {
  audioEnabled: boolean
  volumeLevel: number
}

export default function useGameAudio({ audioEnabled, volumeLevel }: GameAudioOptions) {
  const globalScale = 0.5
  const sfxVolume = audioEnabled ? Math.min(volumeLevel / 100, 1) * globalScale : 0
  const soundConfig = useMemo(() => ({ volume: sfxVolume }), [sfxVolume])
  const softSoundConfig = useMemo(
    () => ({ volume: audioEnabled ? sfxVolume * 0.45 : 0 }),
    [audioEnabled, sfxVolume]
  )
  const musicConfig = useMemo(
    () => ({
      ...soundConfig,
      loop: true,
      volume: audioEnabled ? sfxVolume * 0.25 : 0,
    }),
    [soundConfig, audioEnabled, sfxVolume]
  )

  const audioBase = '/audio/sine'
  const musicPack = '/audio/music'

  const [musicIsPlaying, setMusicIsPlaying] = useState<boolean>(false)
  const musicPlayingRef = useRef<boolean>(false)
  const [playMusicBase, { stop: stopMusicBase }] = useSound(`/audio/space_jazz.mp3`, musicConfig)

  const [playPop1Base] = useSound(`${audioBase}/select.wav`, soundConfig)
  const [playPop2Base] = useSound(`${audioBase}/select.wav`, soundConfig)
  const [playCelebrateBase] = useSound(`${audioBase}/correct.mp3`, soundConfig)
  const [playSelectBase] = useSound(`${audioBase}/select.wav`, soundConfig)
  const [playWrongBase] = useSound(`${audioBase}/wrong.mp3`, soundConfig)
  const [playStartBase] = useSound(`${musicPack}/bell.mp3`, soundConfig)
  const [playWinBase] = useSound(`${musicPack}/win_seq.mp3`, soundConfig)
  const [playLossBase] = useSound(`${musicPack}/fail.mp3`, soundConfig)
  const [playBoopBase] = useSound(`${audioBase}/notification.wav`, soundConfig)
  const [playClick1Base] = useSound(`${audioBase}/tap_03.wav`, soundConfig)
  const [playClick2Base] = useSound(`${audioBase}/tap_03.wav`, soundConfig)
  const [playButtonBase] = useSound(`${audioBase}/button.wav`, soundConfig)
  const [playSwipeBase] = useSound(`${audioBase}/swipe_1.mp3`, softSoundConfig)
  const [playToggleOnBase] = useSound(`${audioBase}/toggle_on.wav`, soundConfig)
  const [playToggleOffBase] = useSound(`${audioBase}/toggle_off.wav`, soundConfig)

  const withHaptic = (play: () => void, pattern?: HapticPattern) => () => {
    play()
    triggerHaptic(pattern)
  }

  const playMusic = () => {
    if (musicPlayingRef.current) {
      stopMusicBase()
    }
    playMusicBase()
    musicPlayingRef.current = true
    setMusicIsPlaying(true)
  }

  const stopMusic = () => {
    stopMusicBase()
    musicPlayingRef.current = false
    setMusicIsPlaying(false)
  }

  const playPop1 = withHaptic(playPop1Base, { kind: 'selection' })
  const playPop2 = withHaptic(playPop2Base, { kind: 'selection' })
  const playCelebrate = withHaptic(playCelebrateBase, {
    kind: 'notification',
    type: NotificationType.Success,
  })
  const playSelect = withHaptic(playSelectBase, { kind: 'selection' })
  const playWrong = withHaptic(playWrongBase, { kind: 'selection' })
  const playStart = withHaptic(playStartBase, { kind: 'impact', style: ImpactStyle.Medium })
  const playWin = withHaptic(playWinBase, {
    kind: 'notification',
    type: NotificationType.Success,
  })
  const playLoss = withHaptic(playLossBase, { kind: 'notification', type: NotificationType.Error })
  const playBoop = withHaptic(playBoopBase, { kind: 'selection' })
  const playClick1 = withHaptic(playClick1Base, { kind: 'selection' })
  const playClick2 = withHaptic(playClick2Base, { kind: 'selection' })
  const playButton = withHaptic(playButtonBase, { kind: 'selection' })
  const playSwipe = withHaptic(playSwipeBase, { kind: 'selection' })
  const playToggleOn = withHaptic(playToggleOnBase, { kind: 'selection' })
  const playToggleOff = withHaptic(playToggleOffBase, { kind: 'selection' })

  return {
    musicIsPlaying,
    playMusic,
    stopMusic,
    playPop1,
    playPop2,
    playCelebrate,
    playSelect,
    playWrong,
    playStart,
    playWin,
    playLoss,
    playBoop,
    playClick1,
    playClick2,
    playButton,
    playSwipe,
    playToggleOn,
    playToggleOff,
  }
}
